import { useState } from 'react';
import { useLoaderData, useLocation } from 'react-router';

import type { loader } from '@modules/professional/page/loaders/overview-page-loader.server';
import { CategoryPills } from '~/ui-rocco/pages/home-page/components/CategoryPills';
import type { CategoryPill } from '~/ui-rocco/pages/home-page/components/CategoryPills';
import { useProfessionalLayoutData } from '@modules/professional/hook/use-professional-layout-data';
import {
    professionalCityLinks,
    professionalLocationHref,
} from '@modules/professional/util/professional-city-links';
import { professionalTabBase } from '@modules/professional/util/professional-tab-base';
import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { ABOUT_SECTION_HEADING_CLASS } from './about-section-heading';

/**
 * The Services pills over the Locations-we-service list, on the About tab
 * under the practice. The company summary that led them sits in the profile
 * header under the tabs.
 *
 * Mobile only. On desktop both are carried by the profile summary header
 * under the banner, which every tab shares. The About route serves the
 * overview's data, which is where the services come from.
 */
export const ProfessionalSummarySection = () => {
    const { professionalOverview } = useLoaderData<typeof loader>();
    // Preview-aware: the same payload under the public route and the Business
    // Manager preview, with any in-flight edit patch applied.
    const layoutData = useProfessionalLayoutData();
    const { pathname } = useLocation();
    const [showAllLocations, setShowAllLocations] = useState(false);

    const services: CategoryPill[] = (professionalOverview.Services ?? [])
        .slice(0, 8)
        .map((service) => ({ label: service }))
        .filter((pill) => pill.label !== '');

    // Each city opens the contact tab on its branch, matching the desktop row.
    const urlSegment = layoutData?.initial?.URLSegment;
    const tabBase = urlSegment
        ? professionalTabBase(urlSegment, pathname)
        : undefined;
    const locations = professionalCityLinks(
        layoutData?.initial?.LibraryLocations,
    );

    // Big franchises (e.g. Resene) service dozens of cities — show the first few
    // and reveal the rest behind a toggle so the list doesn't run the page long.
    const LOCATION_LIMIT = 5;
    const visibleLocations = showAllLocations
        ? locations
        : locations.slice(0, LOCATION_LIMIT);
    const hiddenLocationCount = locations.length - LOCATION_LIMIT;

    return (
        <div className="md:hidden">
            {services.length > 0 || locations.length > 0 ? (
                <div className="mx-auto flex w-full max-w-(--ap-page-width) bg-ground max-md:flex-col max-md:gap-[2.75rem] max-md:px-6 max-md:pt-8 md:gap-[7.2rem] md:px-[3rem]">
                    {services.length > 0 ? (
                        <CategoryPills
                            label="Services"
                            items={services}
                            // Static tags, not links — default cursor (not the
                            // text I-beam), in a fixed 350px (18.229vw @1920) box.
                            className="cursor-default md:w-[21.875rem]"
                            // The About intro's type, as every head on the
                            // tab is set.
                            labelClassName={cn(
                                'md:text-[0.9375rem]',
                                ABOUT_SECTION_HEADING_CLASS,
                                'max-md:opacity-100',
                            )}
                        />
                    ) : null}
                    {locations.length > 0 ? (
                        <div className="min-w-0">
                            <p
                                className={cn(
                                    'm-0 font-sans leading-snug text-[#767676] md:text-[0.9375rem]',
                                    ABOUT_SECTION_HEADING_CLASS,
                                )}
                            >
                                Locations we service
                            </p>
                            {/* Expanded, the full list can run 40+ cities long, so
                                on desktop it flows into 3 balanced columns
                                (multicol fills top-to-bottom, last column short);
                                collapsed and mobile stay a single stack. */}
                            <ul
                                className={`m-0 mt-[1.2rem] list-none p-0 max-md:mt-3 ${
                                    showAllLocations
                                        ? 'md:columns-3 md:[column-gap:3rem]'
                                        : ''
                                }`}
                            >
                                {visibleLocations.map(
                                    ({ city, locationID }) => (
                                        <li
                                            key={city}
                                            // Uppercase ~12px to match the Figma (node
                                            // 2354:55672) — mirrors the Services pills
                                            // beside it, not the old 20px list.
                                            className="mb-[0.1875rem] break-inside-avoid font-sans leading-normal text-[#1f1f21] uppercase max-md:mb-1 max-md:text-xs md:text-[0.75rem]"
                                        >
                                            {tabBase ? (
                                                <RoccoLink
                                                    to={professionalLocationHref(
                                                        tabBase,
                                                        locationID,
                                                    )}
                                                    className="transition-opacity hover:opacity-70"
                                                >
                                                    {city}
                                                </RoccoLink>
                                            ) : (
                                                city
                                            )}
                                        </li>
                                    ),
                                )}
                            </ul>
                            {hiddenLocationCount > 0 ? (
                                <button
                                    type="button"
                                    onClick={() =>
                                        setShowAllLocations((v) => !v)
                                    }
                                    className="mt-[0.6rem] w-fit font-sans leading-normal text-[#767676] uppercase underline underline-offset-2 transition-colors hover:text-[#1f1f21] max-md:mt-1 max-md:text-xs md:text-[0.75rem]"
                                >
                                    {showAllLocations
                                        ? 'Show less'
                                        : `+${hiddenLocationCount} more`}
                                </button>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};
